import { useAuthStore } from '@/stores/authStore';

/**
 * T-68: the app draws before the page-load session check answers, for the person remembered from
 * the last launch (lib/launch-user.ts), who has no token yet. API calls and live sockets wait here
 * until it's safe to send: the check has answered, and either there's a token or nobody is signed
 * in. While the factory can't be reached and the remembered person has no token, they keep waiting
 * (a Retry that gets through lets them go), rather than going out with no token and signing out.
 */

type AuthSnapshot = { sessionChecked: boolean; user: { id: string; token: string } | null };

export function canSend({ sessionChecked, user }: AuthSnapshot): boolean {
  return sessionChecked && (!user || Boolean(user.token));
}

/**
 * What a held request does once the session check has answered:
 * - 'send': go, with this tab's token if it has one.
 * - 'signed-out': the remembered person it was made for turned out signed out. Go without a token: a
 *   public page (an invitation) still gets its answer; a signed-in page gets its 401 as it leaves for
 *   /login, which the app does itself (no second redirect).
 * - 'drop': don't send. Its signal aborted, or someone else signed in: the request was made for what
 *   the remembered person saw, so it never goes out as anyone else.
 */
export type SessionVerdict = 'send' | 'signed-out' | 'drop';

export function whenSessionReady(signal?: AbortSignal | null): Promise<SessionVerdict> {
  const start = useAuthStore.getState();
  if (canSend(start)) return Promise.resolve('send');
  if (signal?.aborted) return Promise.resolve('drop');
  const waitingFor = start.user?.id ?? null;
  return new Promise((resolve) => {
    const done = (verdict: SessionVerdict) => {
      unsubscribe();
      signal?.removeEventListener('abort', onAbort);
      resolve(verdict);
    };
    const onAbort = () => done('drop');
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (!canSend(state)) return;
      if (!waitingFor || state.user?.id === waitingFor) done('send');
      else done(state.user ? 'drop' : 'signed-out');
    });
    signal?.addEventListener('abort', onAbort);
  });
}
